import React, { useState, useEffect, useRef, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import coverLetterTemplateService from '../../../services/coverLetterTemplateService';

const MAX_TEMPLATES = 5;

const CoverLetterTemplateSelector = ({ userId, onSelectTemplate, currentCoverLetter }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editContent, setEditContent] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const dropdownRef = useRef(null);

  const loadTemplates = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const result = await coverLetterTemplateService.getUserTemplates(userId);
      if (result.success) {
        setTemplates(result.data || []);
      }
    } catch (err) {
      console.error('Failed to load cover letter templates:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  // Clear error after 4 seconds
  useEffect(() => {
    if (!error) return;
    const timer = setTimeout(() => setError(null), 4000);
    return () => clearTimeout(timer);
  }, [error]);

const handleSelectTemplate = async (template) => {
  onSelectTemplate(template.content);
  coverLetterTemplateService.useTemplate(template.id, userId).catch((err) => {
    console.error('Failed to track template usage:', err);
  });
  setShowDropdown(false);
};
  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      setError('Please enter a template name');
      return;
    }
    if (!currentCoverLetter?.trim()) {
      setError('Write a cover letter first before saving as a template');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const result = await coverLetterTemplateService.createTemplate(userId, {
        name: templateName.trim(),
        content: currentCoverLetter.trim()
      });

      if (result.success) {
        setTemplateName('');
        setShowSaveForm(false);
        await loadTemplates();
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      const result = await coverLetterTemplateService.deleteTemplate(templateId, userId);
      if (result.success) {
        setDeletingId(null);
        await loadTemplates();
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to delete template');
    }  };

  const handleStartEdit = (template) => {
    setEditingId(template.id);
    setEditName(template.name);
    setEditContent(template.content);
  };

  const handleSaveEdit = async () => {
    if (!editName.trim()) {
      setError('Template name is required');
      return;
    }
    if (!editContent.trim()) {
      setError('Template content is required');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const result = await coverLetterTemplateService.updateTemplate(editingId, userId, {
        name: editName.trim(),
        content: editContent.trim()
      });

      if (result.success) {
        setEditingId(null);
        await loadTemplates();
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to update template');
    } finally {
      setSaving(false);
    }
  };

  const atLimit = templates.length >= MAX_TEMPLATES;

  return (
    <div className="mb-3" ref={dropdownRef}>
      {/* Action Buttons Row */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Use Saved Template Button */}
        <div className="relative">
          <button
            type="button"
            onClick={() => {
              setShowDropdown(!showDropdown);
              setShowSaveForm(false);
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary bg-primary-50 hover:bg-primary-100 border border-primary-200 rounded-lg transition-colors"
          >
            <Icon name="BookOpen" size={14} />
            Use Saved Template
            {templates.length > 0 && (
              <span className="ml-1 text-xs text-primary-600 bg-primary-100 px-1.5 py-0.5 rounded-full">
                {templates.length}
              </span>
            )}
            <Icon name="ChevronDown" size={14} className={`transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
          </button>

          {/* Template Dropdown */}
          {showDropdown && (
            <div className="absolute left-0 top-full mt-1 w-80 bg-white border border-border-primary rounded-lg shadow-lg z-20 max-h-80 overflow-hidden">
              {loading ? (
                <div className="flex items-center justify-center py-6">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
                  <span className="ml-2 text-sm text-text-secondary">Loading...</span>
                </div>
              ) : templates.length === 0 ? (
                <div className="p-4 text-center text-sm text-text-secondary">
                  <Icon name="FileText" size={24} className="mx-auto mb-2 text-text-muted" />
                  <p>No saved templates yet.</p>
                  <p className="text-xs mt-1">Write a cover letter and save it as a template for quick reuse.</p>
                </div>
              ) : (
                <div className="overflow-y-auto max-h-72">
                  <div className="px-3 py-2 border-b border-border-primary bg-background-secondary">
                    <span className="text-xs font-medium text-text-secondary">
                      {templates.length}/{MAX_TEMPLATES} templates saved
                    </span>
                  </div>
                  {templates.map((template) => (
                    <div key={template.id}>
                      {/* Delete confirmation inline */}
                      {deletingId === template.id ? (
                        <div className="px-3 py-3 bg-red-50 border-b border-border-primary">
                          <p className="text-sm text-error font-medium mb-2">Delete "{template.name}"?</p>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => handleDeleteTemplate(template.id)}
                              className="px-3 py-1 text-xs font-medium text-white bg-error hover:bg-red-600 rounded transition-colors"
                            >
                              Delete
                            </button>
                            <button
                              type="button"
                              onClick={() => setDeletingId(null)}
                              className="px-3 py-1 text-xs font-medium text-text-secondary bg-white border border-border-primary hover:bg-background-secondary rounded transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : editingId === template.id ? (
                        /* Edit mode */
                        <div className="px-3 py-3 bg-blue-50 border-b border-border-primary">
                          <div className="space-y-2">
                            <Input
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              placeholder="Template name"
                              className="text-sm"
                            />
                            <textarea
                              value={editContent}
                              onChange={(e) => setEditContent(e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary resize-none"
                              rows={3}
                              maxLength={5000}
                            />
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={handleSaveEdit}
                                disabled={saving}
                                className="px-3 py-1 text-xs font-medium text-white bg-primary hover:bg-primary-600 rounded transition-colors disabled:opacity-50"
                              >
                                {saving ? 'Saving...' : 'Save'}
                              </button>
                              <button
                                type="button"
                                onClick={() => setEditingId(null)}
                                className="px-3 py-1 text-xs font-medium text-text-secondary bg-white border border-border-primary hover:bg-background-secondary rounded transition-colors"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        /* Normal template row */
                        <div className="flex items-start gap-2 px-3 py-2.5 hover:bg-background-secondary border-b border-border-primary last:border-0 group">
                          <button
                            type="button"
                            onClick={() => handleSelectTemplate(template)}
                            className="flex-1 text-left min-w-0"
                          >
                            <div className="text-sm font-medium text-text-primary truncate">
                              {template.name}
                            </div>
                            <div className="text-xs text-text-muted mt-0.5 line-clamp-2">
                              {template.content.slice(0, 100)}{template.content.length > 100 ? '...' : ''}
                            </div>
                          </button>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 pt-0.5">
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); handleStartEdit(template); }}
                              className="p-1 text-text-muted hover:text-primary hover:bg-primary-50 rounded transition-colors"
                              title="Edit template"
                            >
                              <Icon name="Pencil" size={13} />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setDeletingId(template.id); }}
                              className="p-1 text-text-muted hover:text-error hover:bg-red-50 rounded transition-colors"
                              title="Delete template"
                            >
                              <Icon name="Trash2" size={13} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Save as Template Button */}
        {!showSaveForm && (
          <button
            type="button"
            onClick={() => {
              if (atLimit) {
                setError(`You've reached the limit of ${MAX_TEMPLATES} templates. Delete one to save a new one.`);
                return;
              }
              setShowSaveForm(true);
              setShowDropdown(false);
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-text-secondary hover:text-primary bg-background-secondary hover:bg-primary-50 border border-border-primary rounded-lg transition-colors"
            title={atLimit ? `Limit of ${MAX_TEMPLATES} templates reached` : 'Save current cover letter as template'}
          >
            <Icon name="Save" size={14} />
            Save as Template
          </button>
        )}
      </div>

      {/* Save Form (inline) */}
      {showSaveForm && (
        <div className="mt-2 flex items-center gap-2">
          <Input
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="Template name (e.g. General, Tech, Marketing)"
            className="flex-1 text-sm"
            maxLength={100}
          />
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={handleSaveTemplate}
            disabled={saving || !templateName.trim()}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => { setShowSaveForm(false); setTemplateName(''); }}
          >
            Cancel
          </Button>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mt-2 flex items-center gap-1.5 text-sm text-error">
          <Icon name="AlertCircle" size={14} />
          {error}
        </div>
      )}
    </div>
  );
};

export default CoverLetterTemplateSelector;
